import { useState, useEffect } from 'react';
import { X, Percent, Tag, Check } from 'lucide-react';
import { applyDiscount } from '../lib/discounts';
import { DiscountCodeService } from '../services/discountCodeService';
import { useBusiness } from '../contexts/BusinessContext';

interface ApplyDiscountModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  bookingTotal: number;
  onApply: () => Promise<void>;
  existingDiscounts?: Array<{
    id: string;
    title: string;
    type: 'percentage' | 'fixed';
    amount: number;
  }>;
}

export default function ApplyDiscountModal({
  isOpen,
  onClose,
  bookingId,
  bookingTotal,
  onApply,
  existingDiscounts = []
}: ApplyDiscountModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discountMethod, setDiscountMethod] = useState<'manual' | 'code'>('code');
  const [discountCode, setDiscountCode] = useState('');
  const [codeValidation, setCodeValidation] = useState<{
    isValid: boolean;
    code?: any;
    error?: string;
    discountAmount?: number;
  } | null>(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [formData, setFormData] = useState<{
    title: string;
    type: 'percentage' | 'fixed';
    amount: string;
  }>({
    title: '',
    type: 'percentage',
    amount: ''
  });

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
      setDiscountMethod('code');
      setDiscountCode('');
      setCodeValidation(null);
      setValidatingCode(false);
      setFormData({
        title: '',
        type: 'percentage',
        amount: ''
      });
    }
  };

  const calculateDiscountAmount = () => {
    const amount = parseFloat(formData.amount as string) || 0;
    if (formData.type === 'percentage') {
      // Round to 2 decimal places for consistent calculation
      const discountAmount = Math.round((bookingTotal * (amount / 100)) * 100) / 100;
      return Math.min(discountAmount, bookingTotal); // Prevent discount from exceeding total
    }
    return Math.min(amount, bookingTotal); // Prevent discount from exceeding total
  };

  const validateDiscountCode = async (code: string) => {
    if (!selectedBusiness?.id || !code.trim()) {
      setCodeValidation(null);
      return;
    }

    setValidatingCode(true);
    setError(null);

    try {
      const validation = await DiscountCodeService.validateDiscountCode(
        selectedBusiness.id,
        code,
        bookingTotal
      );
      setCodeValidation(validation);
    } catch (error) {
      console.error('Error validating discount code:', error);
      setCodeValidation({
        isValid: false,
        error: 'Failed to validate discount code'
      });
    } finally {
      setValidatingCode(false);
    }
  };

  const handleDiscountCodeChange = (code: string) => {
    setDiscountCode(code);
    
    // Debounce validation
    const timeoutId = setTimeout(() => {
      validateDiscountCode(code);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  const handleApplyDiscountCode = async () => {
    if (!selectedBusiness?.id || !codeValidation?.isValid || !codeValidation.code) return;

    try {
      setLoading(true);
      setError(null);

      const result = await DiscountCodeService.applyDiscountCode(
        bookingId,
        selectedBusiness.id,
        discountCode,
        bookingTotal
      );

      if (result.success) {
        await onApply();
        onClose();
      } else {
        setError(result.error || 'Failed to apply discount code');
      }
    } catch (err) {
      console.error('Error applying discount code:', err);
      setError('Failed to apply discount code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      setError(null);

      await applyDiscount(
        bookingId,
        formData.title,
        formData.type,
        parseFloat(formData.amount) || 0
      );

      await onApply();
      onClose();
    } catch (err) {
      console.error('Error applying discount:', err);
      setError(err instanceof Error ? err.message : 'Failed to apply discount');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[999999]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } z-[999999]`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Apply Discount</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Existing Discount Warning */}
              {existingDiscounts.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 text-purple-800 px-4 py-3 rounded-md text-sm">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-purple-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-purple-800">
                        Existing Discount Will Be Replaced
                      </h3>
                      <div className="mt-2 text-sm text-purple-700">
                        <p>This booking currently has the following discount applied:</p>
                        <ul className="mt-1 list-disc list-inside">
                          {existingDiscounts.map((discount) => (
                            <li key={discount.id}>
                              <strong>{discount.title}</strong> - {discount.type === 'percentage' ? `${discount.amount}%` : `$${discount.amount}`}
                            </li>
                          ))}
                        </ul>
                        <p className="mt-2">
                          Applying a new discount will automatically remove the existing discount and replace it with the new one.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Discount Method Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Discount Method
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={discountMethod === 'code'}
                      onChange={() => setDiscountMethod('code')}
                      className="h-4 w-4 text-gray-900 focus:ring-gray-900"
                    />
                    <span className="ml-2 text-sm text-gray-700 flex items-center">
                      <Tag className="h-4 w-4 mr-1" />
                      Discount Code
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={discountMethod === 'manual'}
                      onChange={() => setDiscountMethod('manual')}
                      className="h-4 w-4 text-gray-900 focus:ring-gray-900"
                    />
                    <span className="ml-2 text-sm text-gray-700 flex items-center">
                      <Percent className="h-4 w-4 mr-1" />
                      Manual Discount
                    </span>
                  </label>
                </div>
              </div>

              {/* Discount Code Input */}
              {discountMethod === 'code' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Code
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={discountCode}
                      onChange={(e) => handleDiscountCodeChange(e.target.value.toUpperCase())}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm font-mono uppercase"
                      placeholder="Enter discount code"
                    />
                    {validatingCode && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin"></div>
                      </div>
                    )}
                    {codeValidation?.isValid && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                    )}
                  </div>
                  
                  {/* Code Validation Feedback */}
                  {codeValidation && !validatingCode && (
                    <div className={`mt-2 text-sm ${codeValidation.isValid ? 'text-green-600' : 'text-red-600'}`}>
                      {codeValidation.isValid ? (
                        <div className="space-y-1">
                          <div className="flex items-center">
                            <Check className="h-4 w-4 mr-1" />
                            Valid discount code
                          </div>
                          {codeValidation.code?.description && (
                            <div className="text-gray-600 text-xs">
                              {codeValidation.code.description}
                            </div>
                          )}
                          <div className="text-xs">
                            Discount: {codeValidation.code?.type === 'percentage' 
                              ? `${codeValidation.code.value}%` 
                              : `$${codeValidation.code?.value}`}
                            {codeValidation.discountAmount && (
                              <span className="ml-2 font-medium">
                                (-${codeValidation.discountAmount.toFixed(2)})
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <X className="h-4 w-4 mr-1" />
                          {codeValidation.error}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Manual Discount Fields */}
              {discountMethod === 'manual' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Name
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Enter discount name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Type
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={formData.type === 'percentage'}
                          onChange={() => setFormData(prev => ({ ...prev, type: 'percentage' }))}
                          className="h-4 w-4 text-gray-900 focus:ring-gray-900"
                        />
                        <span className="ml-2 text-sm text-gray-700">Percentage</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={formData.type === 'fixed'}
                          onChange={() => setFormData(prev => ({ ...prev, type: 'fixed' }))}
                          className="h-4 w-4 text-gray-900 focus:ring-gray-900"
                        />
                        <span className="ml-2 text-sm text-gray-700">Fixed Amount</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {formData.type === 'percentage' ? 'Percentage' : 'Amount'}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">
                          {formData.type === 'percentage' ? '%' : '$'}
                        </span>
                      </div>
                      <input
                        type="number"
                        min="0"
                        step={formData.type === 'percentage' ? '1' : '0.01'}
                        max={formData.type === 'percentage' ? "100" : undefined}
                        value={formData.amount}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          amount: e.target.value
                        }))}
                        className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      />
                    </div>
                  </div>

                  {parseFloat(formData.amount as string) > 0 && (
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-600">
                        Discount amount: ${calculateDiscountAmount().toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        New total: ${(bookingTotal - calculateDiscountAmount()).toLocaleString()}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Discount Code Preview */}
              {discountMethod === 'code' && codeValidation?.isValid && codeValidation.discountAmount && (
                <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-sm text-green-800 font-medium mb-2">
                    Discount Preview
                  </div>
                  <div className="text-sm text-green-700">
                    Discount amount: ${codeValidation.discountAmount.toFixed(2)}
                  </div>
                  <div className="text-sm text-green-700 mt-1">
                    New total: ${(bookingTotal - codeValidation.discountAmount).toFixed(2)}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 bg-white">
            <button
              onClick={discountMethod === 'code' ? handleApplyDiscountCode : handleApplyDiscount}
              disabled={
                loading || 
                (discountMethod === 'manual' && (!formData.title || !formData.amount || parseFloat(formData.amount) <= 0)) ||
                (discountMethod === 'code' && (!codeValidation?.isValid || !discountCode.trim()))
              }
              className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Applying...' : `Apply ${discountMethod === 'code' ? 'Discount Code' : 'Discount'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}